<template>
  <y-alert v-if="apiResults.payout.state.error" type="error">
    {{ t("payouts.ledger.errorMessage", { email: "help@yetipay.me" }) }}
  </y-alert>
  <v-skeleton-loader v-else-if="apiResults.payout.state.loading" class="ma-2" type="chip" />
  <div v-else class="d-flex flex-row flex-wrap">
    <v-chip class="ma-2" color="green darken-4" text-color="white">
      {{ t("payouts.ledger.headers.amount") }}: {{ displayAmount(apiResults.payout.data!.amount) }}
    </v-chip>
    <v-chip class="ma-2" color="#EA4335" text-color="#EA4335" variant="outlined">
      {{ t("payouts.ledger.headers.deducted") }}: {{ displayAmount(deducted) }}
    </v-chip>
    <v-chip class="ma-2" color="deep-purple accent-4" variant="outlined">
      {{ t("payouts.ledger.headers.reference") }}:&nbsp;<kbd>{{ apiResults.payout.data!.id }}</kbd>
    </v-chip>
    <v-chip class="ma-2" color="indigo darken-4" variant="outlined">
      {{ t("payouts.ledger.headers.site") }}: {{ apiResults.payout.data!.site.siteName }}
    </v-chip>
    <v-chip class="ma-2" color="blue-grey accent-4" variant="outlined">
      {{ t("payouts.ledger.headers.date") }}: {{ displayTime(apiResults.payout.data!.date) }}
    </v-chip>
  </div>

  <y-alert
    v-if="
      apiResults.payout.data &&
      apiResults.payout.data.payoutTotalError &&
      apiResults.payout.data.payoutTotalMismatchReason === 'genericPayoutError'
    "
    type="error"
    class="mt-5"
  >
    {{ t("payouts.ledger.totalError") }}
  </y-alert>

  <y-alert
    v-if="
      apiResults.payout.data &&
      apiResults.payout.data.payoutTotalError &&
      apiResults.payout.data.payoutTotalMismatchReason === 'grantOrRepayment'
    "
    type="warning"
    class="mt-5"
  >
    {{ t("payouts.ledger.grantOrRepayment") }}
  </y-alert>

  <div class="mt-5">
    <h3 class="mb-5">
      {{ t("payouts.ledger.dailySummary") }}
    </h3>
    <v-skeleton-loader v-if="apiResults.payout.state.loading" type="table" />
    <DailySummary
      v-else
      :days="dailySummary!"
      :default-currency="apiResults.payout.data!.amount.currency"
    />
  </div>

  <div class="mt-10">
    <div class="d-flex mb-1 align-center justify-space-between">
      <h3>{{ t("payouts.ledger.allMovements") }}</h3>
      <v-btn
        variant="text"
        size="default"
        color="secondary"
        :loading="reportLoading"
        icon="mdi-download"
        @click="downloadReport"
      />
    </div>
    <v-skeleton-loader v-if="apiResults.payout.state.loading" type="table" />
    <v-alert
      v-else-if="apiResults.payout.state.error"
      color="red lighten-2"
      dark
      class="py-3 px-6 ml-3"
    >
      {{ t("payouts.ledger.errorMessage", { email: "help@yetipay.me" }) }}
    </v-alert>
    <template v-else>
      <v-data-table
        :headers="transactionsTableHeaders"
        :items="apiResults.payout.data!.transfers"
        item-value="transferId"
        :sort-by="[{ key: 'bookingDate', order: 'desc' }]"
        disable-sort
        :search="search"
        :custom-filter="filter"
        show-expand
        expand-on-click
        class="elevation-1"
      >
        <template #top>
          <v-toolbar class="pt-5" :height="true || mdAndUp ? '90px' : '200px'">
            <v-container>
              <v-row no-gutters>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="selectedType"
                    :items="typeFilters"
                    :label="t('payouts.ledger.filters.type')"
                    clearable
                    class="mr-2"
                  >
                    <template #item="{ props, item }">
                      <v-list-item v-bind="props" title="" class="text-uppercase">
                        {{ item.value }}
                      </v-list-item>
                    </template>
                    <template #selection="{ item }">
                      <span class="text-uppercase">{{ item.value }}</span>
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="selectedPaymentMethod"
                    :items="paymentMethodFilters"
                    :label="t('payouts.ledger.filters.method')"
                    clearable
                    class="mr-2"
                  >
                    <template #item="{ item, props }">
                      <v-list-item v-bind="props" title="">
                        <payment-method :method="item.value" />
                      </v-list-item>
                    </template>
                    <template #selection="{ item }">
                      <payment-method :method="item.value" />
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="12" md="4">
                  <v-chip class="mt-4" color="grey darken-4" variant="outlined">
                    {{ t("payouts.ledger.filteredTotal") }}:
                    {{ displayAmount(filteredTotal) }}
                  </v-chip>
                </v-col>
              </v-row>
            </v-container>
          </v-toolbar>
        </template>

        <template #item.amount="{ item }">
          {{
            displayAmount(
              item.amount.value + (item.includedTip?.value ?? 0) + (item.additionalTip?.value ?? 0),
            )
          }}
        </template>
        <template #item.tip="{ item }">
          {{
            displayAmount(item.includedTip ?? (item.additionalTip ? item.additionalTip : null) ?? 0)
          }}
        </template>
        <template #item.bookingDate="{ item }">
          {{ displayTime(item.bookingDate) }}
        </template>
        <template #item.type="{ item }">
          <span class="text-uppercase">{{ item.type }}</span>
        </template>
        <template #item.pspPaymentReference="{ item }">
          <span
            v-if="
              ['fees', 'internal transfer', 'yetipay fees', 'grant', 'repayment'].includes(
                item.type,
              )
            "
          >
            {{ t("views.payouts.payoutLedger.id") }} <kbd>{{ item.transferId }}</kbd>
          </span>
          <span v-else-if="item.pspPaymentReference">
            {{ t("views.payouts.payoutLedger.psp") }} <kbd>{{ item.pspPaymentReference }}</kbd>
          </span>
          <v-tooltip v-else position="bottom">
            <template #activator="{ props }">
              <v-icon class="opacity-2" v-bind="props"> error </v-icon>
            </template>
            <span>{{ t("payouts.ledger.pspReferenceError") }}</span>
          </v-tooltip>
        </template>
        <template #item.paymentMethod="{ item }">
          <v-skeleton-loader v-if="apiResults.transactions.state.loading" type="text" />
          <span v-else-if="apiResults.transactions.state.error">
            <v-tooltip position="bottom">
              <template #activator="{ props }">
                <v-icon class="opacity-2" v-bind="props">error</v-icon>
              </template>
              <span>{{ t("payouts.ledger.refreshError") }}</span>
            </v-tooltip>
          </span>
          <span v-else-if="apiResults.transactions.data![item.pspPaymentReference]">
            <payment-method
              :method="apiResults.transactions.data![item.pspPaymentReference].paymentMethod"
            />
          </span>
          <span v-else>
            <v-tooltip position="bottom">
              <template #activator="{ props }">
                <v-icon class="opacity-2" v-bind="props">horizontal_rule</v-icon>
              </template>
              <span>{{ t("payouts.ledger.transferNotRelated") }}</span>
            </v-tooltip>
          </span>
        </template>
        <template #expanded-row="{ columns, item }">
          <tr>
            <td
              v-if="
                apiResults.transactions.data &&
                apiResults.transactions.data[item.pspPaymentReference]
              "
              :colspan="columns.length"
              class="pa-4"
            >
              <div v-if="apiResults.transactions.data[item.pspPaymentReference].paymentLinkId">
                <strong>{{ t("payouts.ledger.paymentLinkId") }}:</strong>&nbsp;
                <kbd>{{
                  apiResults.transactions.data[item.pspPaymentReference].paymentLinkId
                }}</kbd>
              </div>
              <div>
                <strong>{{ t("payouts.ledger.transactionId") }}:</strong>&nbsp;
                <kbd>{{
                  apiResults.transactions.data[item.pspPaymentReference].transactionId
                }}</kbd>
              </div>
              <div>
                <strong>{{ t("payouts.ledger.merchantReference") }}:</strong>
                {{ apiResults.transactions.data[item.pspPaymentReference].adyenMerchantReference }}
              </div>
            </td>
          </tr>
        </template>
      </v-data-table>
    </template>
  </div>

  <v-btn-nd class="mt-5" to="/payouts">
    <template #prepend>
      <v-icon class="opacity-10"> mdi-arrow-left </v-icon>
    </template>
    {{ t("payouts.ledger.backButton") }}
  </v-btn-nd>
  <DownloadDialog v-model="reportDownloadDialog" :report-id="reportId" />
</template>

<script setup lang="ts">
import BigNumber from "bignumber.js";
import { format, parseISO } from "date-fns";
import { inject, computed, ref, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useDisplay } from "vuetify";
import { Amount } from "@/api/merchant-service/common-types";
import * as Payout from "@/api/merchant-service/payout";
import { TransactionReducedV1 } from "@/api/merchant-service/transaction";
import { API } from "@/plugins/api";

const api = inject<API>("api");
const { t } = useI18n();

const route = useRoute();
const { mdAndUp } = useDisplay();

const transactionsTableHeaders = ref([
  { title: t("payouts.ledger.headers.reference"), value: "pspPaymentReference" },
  { title: t("payouts.ledger.headers.type"), value: "type" },
  { title: t("payouts.ledger.headers.amount"), value: "amount" },
  { title: t("payouts.ledger.headers.tip"), value: "tip" },
  { title: t("payouts.ledger.headers.paymentMethod"), value: "paymentMethod" },
  { title: t("payouts.ledger.headers.date"), value: "bookingDate" },
]);
const typeFilters = ref([
  "transaction",
  "refund",
  "chargeback",
  "internal transfer",
  "fees",
  "grant",
  "repayment",
]);

const apiResults = ref<{
  payout: { data: Payout.PayoutLedger | undefined; state: { loading: boolean; error: boolean } };
  transactions: {
    data: Record<string, TransactionReducedV1> | undefined;
    state: { loading: boolean; error: boolean };
  };
}>({
  payout: { data: undefined, state: { loading: true, error: false } },
  transactions: { data: undefined, state: { loading: true, error: false } },
});

onMounted(async () => {
  apiResults.value.payout.state.loading = true;
  try {
    apiResults.value.payout.data = await api!.payout.getPayout(route.params.payoutId as string);
    apiResults.value.payout.data.transfers = apiResults.value.payout.data.transfers.map((t) => {
      return { ...t, day: t.bookingDate.split("T")[0] };
    });
  } catch (error) {
    console.error(error);
    apiResults.value.payout.state.error = true;
  } finally {
    apiResults.value.payout.state.loading = false;
  }
});

const selectedType = ref(undefined);
const selectedPaymentMethod = ref(undefined);
const filteredTotal = computed(() =>
  apiResults.value.payout
    .data!.transfers.filter((item) => filter(null, null, { value: undefined, raw: item }))
    .reduce(
      (acc, item) =>
        acc + item.amount.value + (item.includedTip?.value ?? 0) + (item.additionalTip?.value ?? 0),
      0,
    ),
);
const paymentMethodFilters = computed(() => {
  if (!apiResults.value.transactions.data) {
    return [];
  }
  const days = Object.keys(apiResults.value.transactions.data);
  const paymentMethods = days.map((k) => apiResults.value.transactions.data![k].paymentMethod);
  return [...new Set(paymentMethods)];
});
const payoutCurrency = computed<string | undefined>(
  () => apiResults.value.payout.data?.amount.currency,
);
const search = computed(() => selectedType.value + " " + selectedPaymentMethod.value);
const dailySummary = computed<Payout.DailySummary[] | undefined>(() => {
  // We need to reformat the data sent to the DailySummary component because the API returns a different format...
  return apiResults.value.payout.data?.payoutSummary.map((day): Payout.DailySummary => {
    const createAmount = (value: number): Amount => ({
      value: new BigNumber(value).dividedBy(100).toNumber(),
      currency: payoutCurrency.value!,
    });
    return {
      date: format(parseISO(day.date), "yyy.MM.dd"),
      totalProcessed: createAmount(day.totalProcessed),
      totalRefunded: createAmount(day.totalRefunded),
      totalChargeback: createAmount(day.totalChargeback),
      includedTip: day.includedTip ? createAmount(day.includedTip) : undefined,
      additionalTip: day.additionalTip ? createAmount(day.additionalTip) : undefined,
      totalMisc: createAmount(day.totalMisc),
      totalFeeDeductions: createAmount(day.totalFeeDeductions),
      totalGranted: day.totalGranted !== undefined ? createAmount(day.totalGranted) : undefined,
      totalRepaid: day.totalRepaid !== undefined ? createAmount(day.totalRepaid) : undefined,
      subtotal: createAmount(day.subtotal),
    };
  });
});
const deducted = computed(() =>
  apiResults.value.payout.data!.payoutSummary.reduce(
    (acc: number, item) =>
      acc +
      (item.totalRefunded +
        item.totalFeeDeductions +
        item.totalChargeback +
        item.totalMisc -
        (item.additionalTip ?? 0)),
    0,
  ),
);

const reportId = ref<string | undefined>(undefined);
const reportDownloadDialog = ref(false);
const reportLoading = ref(false);
const downloadReport = async () => {
  reportLoading.value = true;
  try {
    const result = await api!.payout.generatePayoutDetailReport(route.params.payoutId as string);
    reportId.value = result;
    reportDownloadDialog.value = true;
  } catch (error) {
    console.error(error);
  } finally {
    reportLoading.value = false;
  }
};

watch(
  () => apiResults.value.payout.data,
  async () => {
    apiResults.value.transactions.state.loading = true;
    const pspReferences = apiResults.value.payout
      .data!.transfers.filter((t) => t.pspPaymentReference)
      .map((t) => t.pspPaymentReference);
    try {
      apiResults.value.transactions.data = (
        await api!.transaction.getTransactionsFromPsp(pspReferences)
      ).reduce<Record<string, TransactionReducedV1>>((acc, item) => {
        acc[item.pspReference] = item;
        return acc;
      }, {});
    } catch (error) {
      console.error(error);
      apiResults.value.transactions.state.error = true;
    } finally {
      apiResults.value.transactions.state.loading = false;
    }
  },
);

const filter = (
  value: unknown,
  query: unknown,
  item?: { value: unknown; raw: Payout.Transfer },
) => {
  if (!item?.raw.type) return false;
  let keep = selectedType.value ? item.raw.type === selectedType.value : true;
  keep =
    keep && selectedPaymentMethod.value && apiResults.value.transactions.data
      ? apiResults.value.transactions.data[item.raw.pspPaymentReference] &&
        apiResults.value.transactions.data[item.raw.pspPaymentReference].paymentMethod ===
          selectedPaymentMethod.value
      : keep;
  return keep;
};

const displayAmount = (amount: Amount | number) => {
  const _value = typeof amount === "number" ? amount : amount.value;
  const _currency = typeof amount === "number" ? payoutCurrency.value! : amount.currency;
  return new BigNumber(_value)
    .dividedBy(100)
    .toNumber()
    .toLocaleString("en-UK", { style: "currency", currency: _currency });
};

const displayTime = (time: string) => format(parseISO(time), "yyy.MM.dd HH:mm");
</script>
